import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./fonts.css";
import {
  RouterProvider,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";
import { router } from "./routing";
import * as Sentry from "@sentry/react";
import ReactGAModule from "react-ga4";
const ReactGA = (ReactGAModule as any).default ?? ReactGAModule;
import { ApolloProvider } from "@apollo/client/react";
import { apolloClient } from "./graphql/apolloClient";
import l10n from "./contextProviders/getFluentLocalizationContext";
import { LocalizationProvider } from "@fluent/react";
import { HelmetProvider } from "react-helmet-async";
import { TutorialProvider } from "./components/tutorial/TutorialContext";
import { ThemeVars, lightTheme } from "@atlas-int/tokens";

const {
  REACT_APP_SENTRY_DSN,
  REACT_APP_SENTRY_ENV,
  REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
  REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  REACT_APP_GOOGLE_ANALYTICS_GA4_STREAM_ID,
} = import.meta.env;

if (REACT_APP_SENTRY_DSN && REACT_APP_SENTRY_ENV) {
  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay for debugging user-reported issues
      Sentry.replayIntegration(),
    ],
    environment: REACT_APP_SENTRY_ENV,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: REACT_APP_SENTRY_TRACES_SAMPLE_RATE
      ? parseFloat(REACT_APP_SENTRY_TRACES_SAMPLE_RATE)
      : 0.8,
    // Session Replay sample rates
    // Capture 10% of sessions for replay
    replaysSessionSampleRate: REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
      ? parseFloat(REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE)
      : 0.1,
    // Always capture replays when an error occurs
    replaysOnErrorSampleRate: REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
      ? parseFloat(REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE)
      : 1.0,
  });
} else {
  console.log("Build is running without Sentry.");
}

if (REACT_APP_GOOGLE_ANALYTICS_GA4_STREAM_ID) {
  ReactGA.initialize([
    {
      trackingId: REACT_APP_GOOGLE_ANALYTICS_GA4_STREAM_ID,
    },
  ]);
}

const container = document.getElementById("root")!;
const root = createRoot(container);

// Note: React.StrictMode is disabled because it causes double-rendering
// which interferes with WebGL state management in country-profiles visualizations
root.render(
  <HelmetProvider>
    <ThemeVars theme={import.meta.env.PROD ? lightTheme : undefined} />
    <TutorialProvider>
      <ApolloProvider client={apolloClient}>
        <LocalizationProvider l10n={l10n}>
          {/* @ts-ignore */}
          <RouterProvider router={router} />
        </LocalizationProvider>
      </ApolloProvider>
    </TutorialProvider>
  </HelmetProvider>,
);
